import { useLightMode } from '@/hooks/useLightMode'

import { Box, Button, ButtonProps, HStack, Text } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

interface MediaToggleIProps {
  /** Live button filled with accent color */
  isLiveActive?: boolean
  /** Clip button filled with accent color (ignored if isClipLoading is true) */
  isClipActive?: boolean
  /** Clip button shows loading spinner and is disabled */
  isClipLoading?: boolean
  /** Label for clip button when not loading (default: 'Clip available') */
  clipLabel?: string
  /** Extended button filled with accent color */
  isExtendedActive?: boolean
  /** Show the Extended (forensic clip) button */
  showExtended?: boolean
  /** Label for the Extended button (default: 'Extended') */
  extendedLabel?: string
  onLiveClick: () => void
  onClipClick: () => void
  onExtendedClick?: () => void
}

const spinKeyframes = keyframes`
  to { transform: rotate(360deg); }
`

const baseButtonStyles: ButtonProps = {
  border: 'none',
  borderRadius: '0',
  fontSize: '12px',
  fontWeight: '600',
  h: 'auto',
  lineHeight: '1',
  minW: 'auto',
  px: '14px',
  py: '6px',
  _focus: { boxShadow: 'none' },
  _focusVisible: { boxShadow: 'none' },
}

export const MediaToggle = ({
  isLiveActive = false,
  isClipActive = false,
  isClipLoading = false,
  clipLabel = 'Clip available',
  isExtendedActive = false,
  showExtended = false,
  extendedLabel = 'Extended',
  onLiveClick,
  onClipClick,
  onExtendedClick,
}: MediaToggleIProps) => {
  const mode = useLightMode()

  // Theme tokens
  const accent = `interactive.primary.${mode}`
  const accentText = `text.on-accent.${mode}`

  // Accent opacity variants (not available in theme)
  const accentBase = mode === 'light' ? '20, 184, 166' : '221, 254, 85'
  const accentAlpha = (opacity: number) => `rgba(${accentBase}, ${opacity})`

  // Clip button states
  const isClipReady = !isClipLoading && !isClipActive
  const glowShadow = `inset 0 0 0 1px ${accentAlpha(0.35)}`

  return (
    <HStack
      bg={`bg.input.${mode}`}
      border='1px solid'
      borderColor={`border.default.${mode}`}
      borderRadius='0'
      gap='2px'
      p='2px'
      spacing='0px'
      w='min'
    >
      <Button
        {...baseButtonStyles}
        _hover={
          isLiveActive
            ? { bg: accent, color: accentText }
            : { color: `text.secondary.${mode}`, bg: accentAlpha(0.08) }
        }
        bg={isLiveActive ? accent : 'transparent'}
        color={isLiveActive ? accentText : `text.tertiary.${mode}`}
        data-testid='expand-livestream-button'
        onClick={onLiveClick}
      >
        Live
      </Button>

      <Button
        {...baseButtonStyles}
        _disabled={{ opacity: 1, cursor: 'default' }}
        _hover={
          isClipLoading
            ? { bg: accentAlpha(0.08), color: accent }
            : {
                bg: isClipActive ? accent : accentAlpha(0.12),
                color: isClipActive ? accentText : accent,
                boxShadow: isClipReady ? glowShadow : undefined,
              }
        }
        bg={
          isClipLoading
            ? accentAlpha(0.08)
            : isClipActive
            ? accent
            : accentAlpha(0.12)
        }
        boxShadow={isClipReady ? glowShadow : undefined}
        color={isClipActive ? accentText : accent}
        cursor={isClipLoading ? 'default' : 'pointer'}
        data-testid={
          isClipLoading ? 'retrieving-clip-button' : 'clip-available-button'
        }
        disabled={isClipLoading}
        onClick={isClipLoading ? undefined : onClipClick}
      >
        <HStack spacing='6px'>
          {isClipLoading && (
            <Box
              animation={`${spinKeyframes} 0.8s linear infinite`}
              border='1.5px solid'
              borderColor={accentAlpha(0.25)}
              borderRadius='50%'
              borderTopColor={accent}
              flexShrink={0}
              h='10px'
              w='10px'
            />
          )}
          <Text as='span'>
            {isClipLoading ? 'Retrieving clip…' : clipLabel}
          </Text>
        </HStack>
      </Button>

      {showExtended && (
        <Button
          {...baseButtonStyles}
          _hover={
            isExtendedActive
              ? { bg: accent, color: accentText }
              : { color: `text.secondary.${mode}`, bg: accentAlpha(0.08) }
          }
          bg={isExtendedActive ? accent : 'transparent'}
          color={isExtendedActive ? accentText : `text.tertiary.${mode}`}
          data-testid='extended-clip-button'
          onClick={onExtendedClick}
        >
          {extendedLabel}
        </Button>
      )}
    </HStack>
  )
}
